import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { DEFAULT_CONTRACT } from 'shared/constants/contracts';
import { useContractInventoryFormState } from 'shared/contexts/contract-inventory-form-context';
import { useCreateContract } from 'shared/mutations/create-contract';
import { useUploadPdfFiles } from 'shared/mutations/pdf-upload-mutation';
import { ContractDocument } from 'shared/types';
import { ContractUploadFormData } from 'shared/types/forms';
import { ContractRouteParams } from 'shared/types/routing';
import { addPdfExtension, hashFileName } from 'shared/utility';
import { ContractUploadFields } from './contractUploadFields';

export function ContractUploadDialog() {
  const { masterCustomer, abgBrand } = useParams<ContractRouteParams>();
  const { newContractUploadDialogOpen, toggleNewContractUploadDialog } = useContractInventoryFormState();
  const createContractMutation = useCreateContract();
  const uploadPdfMutation = useUploadPdfFiles();

  const methods = useForm<ContractUploadFormData>({
    defaultValues: DEFAULT_CONTRACT,
  });
  const { handleSubmit, reset } = methods;

  useEffect(() => {
    reset(DEFAULT_CONTRACT);
  }, [newContractUploadDialogOpen, reset]);

  const onClose = useCallback(() => {
    toggleNewContractUploadDialog();
  }, [toggleNewContractUploadDialog]);

  const onSubmit = useCallback(
    async (data: ContractUploadFormData) => {
      const brand = data.isBrandOnly ? abgBrand : data.brands?.join(';') ?? abgBrand;
      const originalDocumentName = data.files?.[0].name || '';
      const contractId = hashFileName(`${masterCustomer}${brand}${data.billToCode}${originalDocumentName}`);
      const formData = new FormData();
      const fileToUpload = data.files ? [data.files[0]] : [];
      formData.append('file', new File(fileToUpload, addPdfExtension(contractId)));

      await uploadPdfMutation.mutateAsync(formData);
      const newContract: ContractDocument = {
        id: contractId,
        customerName: masterCustomer,
        brand,
        billToCode: data.billToCode,
        startDate: data.startDate,
        endDate: data.endDate,
        originalDocumentName,
        growthTiers: data.copyInfofromContract ? data.copyInfofromContract.growthTiers : [],
        volumeTiers: data.copyInfofromContract ? data.copyInfofromContract.volumeTiers : [],
        terms: data.copyInfofromContract ? data.copyInfofromContract.terms : [],
        modifiedBy: '',
        modifiedByEmail: '',
        modifiedDate: '',
        approvedBy: '',
        approvedByEmail: '',
        approvedDate: '',
        l12MProductGrossRevenue: 0,
        status: data.copyInfofromContract ? 'active' : 'draft',
        approvalStatus: 'Pending Approval',
        deletedAt: '',
        deletedBy: '',
        markedForDeletionOrRestorationOn: '',
      };
      await createContractMutation.mutateAsync(newContract);
      onClose();
    },
    [onClose, abgBrand, createContractMutation, masterCustomer, uploadPdfMutation]
  );

  return (
    <Dialog onClose={onClose} open={newContractUploadDialogOpen}>
      <DialogTitle>Contract Upload</DialogTitle>
      <DialogContent>
        <Box sx={{ paddingTop: 1 }}>
          <FormProvider {...methods}>
            <form aria-label='form' id='new-contract-form' onSubmit={handleSubmit(onSubmit)}>
              <ContractUploadFields />
            </form>
          </FormProvider>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          type='submit'
          form='new-contract-form'
          variant='contained'
          color='secondary'
          startIcon={<FileUploadIcon />}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
}
